<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { getCurrentInstance } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import ModalBox from './ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import AccessTokenService from '@/services/AccessTokenService'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'
import { track, EVENTS } from '@/lib/tracking'

const MODAL_ID = 'modalDeleteAccessToken'

const props = defineProps({
	accessToken: {
		type: Object,
		default: () => ({}),
	},
	modalSize: {
		type: String,
		default: 'md',
		validator(v) {
			return ['xl', 'lg', 'md', 'sm'].some((size) => size === v)
		},
	},
	cancelText: {
		type: String,
		default: 'Cancel',
	},
})

const emit = defineEmits(['modal-cancel', 'modal-dismiss', 'toggle-processing', 'token-deleted'])

const { notify } = useNotification()
const root = getCurrentInstance()?.proxy?.$root

const { isVisible, hide } = useLegacyModalBridge(MODAL_ID)

const dismiss = () => {
	emit('modal-cancel')
	hide()
	emit('modal-dismiss')
}

const deleteToken = async () => {
	try {
		emit('toggle-processing', true)
		hide()
		await AccessTokenService.deleteAccessToken(props.accessToken._id)
		track(EVENTS.API_TOKEN_REVOKED, {})
		notify({ group: 'success', text: 'Access token is deleted' })
		emit('token-deleted')
	} catch (err) {
		notify({
			group: 'error',
			title: "Couldn't delete access token",
			text: err.message,
		})

		if (err.message?.includes('upgrade')) {
			showModal('billing-prompt')
		}
	}
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		:size="modalSize"
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:h-auto lg:max-h-none"
		@close="dismiss"
	>
		<div class="px-6 pt-6">
			<h4 class="text-s-xl">Revoke Access Token</h4>
			<p class="text-s-s text-dark-9 mb-2 mt-2">
				The “{{ accessToken.name }}” token access will become invalid for all users. This action cannot be undone.
			</p>
		</div>
		<template #footer>
			<div class="flex justify-end gap-2 p-6 border-t border-surface-3">
				<FormButton type="surface-4" size="md" @click="dismiss">
					{{ cancelText }}
				</FormButton>
				<FormButton type="red" size="md" @click="deleteToken">
					Revoke
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
